/**
 * Schedule Properties Panel
 *
 * Right sidebar panel showing schedule properties, validation results,
 * and quick actions for the selected schedule.
 */

import { useMemo } from 'react';
import type { ParsedSchedule, ScheduleValidationResult } from './types';
import { validateSchedule, getValidationSummary } from './validation';
import { useScheduleStore } from './scheduleStore';
import { useIDFStore } from '../stores/idfStore';

/**
 * Validation status badge
 */
function ValidationBadge({ result }: { result: ScheduleValidationResult }) {
  const summary = getValidationSummary(result);

  if (summary.hasErrors) {
    return (
      <span className="inline-flex items-center gap-1 rounded-full bg-red-100 px-2 py-0.5 text-xs font-medium text-red-700 dark:bg-red-950 dark:text-red-300">
        <svg className="h-3 w-3" viewBox="0 0 24 24" fill="currentColor">
          <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z" />
        </svg>
        {summary.summary}
      </span>
    );
  }

  if (summary.hasWarnings) {
    return (
      <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-700 dark:bg-amber-950 dark:text-amber-300">
        <svg className="h-3 w-3" viewBox="0 0 24 24" fill="currentColor">
          <path d="M1 21h22L12 2 1 21zm12-3h-2v-2h2v2zm0-4h-2v-4h2v4z" />
        </svg>
        {summary.summary}
      </span>
    );
  }

  return (
    <span className="inline-flex items-center gap-1 rounded-full bg-green-100 px-2 py-0.5 text-xs font-medium text-green-700 dark:bg-green-950 dark:text-green-300">
      <svg className="h-3 w-3" viewBox="0 0 24 24" fill="currentColor">
        <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z" />
      </svg>
      Valid
    </span>
  );
}

/**
 * Error/Warning list item
 */
function ValidationItem({
  type,
  message,
  suggestion,
}: {
  type: 'error' | 'warning';
  message: string;
  suggestion?: string;
}) {
  return (
    <div
      className={`rounded-md p-2 text-xs ${
        type === 'error'
          ? 'bg-red-50 text-red-700 dark:bg-red-950 dark:text-red-300'
          : 'bg-amber-50 text-amber-700 dark:bg-amber-950 dark:text-amber-300'
      }`}
    >
      <p className="font-medium">{message}</p>
      {suggestion && <p className="mt-1 text-muted-foreground">{suggestion}</p>}
    </div>
  );
}

interface SchedulePropertiesPanelProps {
  /** Selected schedule */
  schedule: ParsedSchedule | null;
  /** Callback to update schedule properties */
  onUpdate?: (updates: Partial<ParsedSchedule>) => void;
  /** Callback to delete the schedule */
  onDelete?: () => void;
  /** Callback to duplicate the schedule */
  onDuplicate?: () => void;
  /** Whether editing is disabled */
  readOnly?: boolean;
}

/**
 * Schedule Properties Panel Component
 */
export function SchedulePropertiesPanel({
  schedule,
  onUpdate,
  onDelete,
  onDuplicate,
  readOnly = false,
}: SchedulePropertiesPanelProps) {
  const typeLimits = useScheduleStore((state) => state.typeLimits);
  const getScheduleInfo = useScheduleStore((state) => state.getScheduleInfo);
  const document = useIDFStore((state) => state.document);

  // Get type limits for the schedule
  const scheduleTypeLimits = useMemo(() => {
    if (!schedule?.typeLimits) return null;
    return typeLimits.find((tl) => tl.name === schedule.typeLimits) ?? null;
  }, [schedule?.typeLimits, typeLimits]);

  // Validate schedule
  const validation = useMemo(() => {
    if (!schedule) return null;
    return validateSchedule(schedule, scheduleTypeLimits);
  }, [schedule, scheduleTypeLimits]);

  // Get schedule info (references, category)
  const scheduleInfo = useMemo(() => {
    if (!schedule) return null;
    return getScheduleInfo(schedule, document);
  }, [schedule, document, getScheduleInfo]);

  if (!schedule) {
    return (
      <div className="flex h-full flex-col bg-muted/50">
        <div className="border-b bg-card px-4 py-3">
          <h2 className="text-sm font-semibold text-foreground">Properties</h2>
        </div>
        <div className="flex flex-1 flex-col items-center justify-center p-6 text-center">
          <svg
            className="h-12 w-12 text-muted-foreground/50"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.5"
          >
            <rect x="3" y="4" width="18" height="18" rx="2" />
            <path d="M16 2v4M8 2v4M3 10h18" />
          </svg>
          <p className="mt-2 text-sm text-muted-foreground">Select a schedule to view properties</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-full flex-col bg-muted/50">
      {/* Header */}
      <div className="border-b bg-card px-4 py-3">
        <h2 className="text-sm font-semibold text-foreground">Properties</h2>
        {validation && <ValidationBadge result={validation} />}
      </div>

      <div className="flex-1 overflow-y-auto">
        {/* Basic properties */}
        <div className="border-b bg-card p-4">
          <h3 className="mb-3 text-xs font-semibold uppercase text-muted-foreground">Schedule Info</h3>

          <div className="space-y-3">
            {/* Name */}
            <div>
              <label className="block text-xs font-medium text-muted-foreground">Name</label>
              {readOnly ? (
                <p className="mt-1 text-sm text-foreground">{schedule.name}</p>
              ) : (
                <input
                  type="text"
                  value={schedule.name}
                  onChange={(e) => onUpdate?.({ name: e.target.value })}
                  className="mt-1 w-full rounded-md border border-border bg-background px-3 py-1.5 text-sm text-foreground focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
                />
              )}
            </div>

            {/* Type */}
            <div>
              <label className="block text-xs font-medium text-muted-foreground">Type</label>
              <p className="mt-1 text-sm text-foreground">{schedule.scheduleType}</p>
            </div>

            {/* Type Limits */}
            <div>
              <label className="block text-xs font-medium text-muted-foreground">Type Limits</label>
              {readOnly ? (
                <p className="mt-1 text-sm text-foreground">{schedule.typeLimits || '(none)'}</p>
              ) : (
                <select
                  value={schedule.typeLimits || ''}
                  onChange={(e) => onUpdate?.({ typeLimits: e.target.value || null })}
                  className="mt-1 w-full rounded-md border border-border bg-background px-3 py-1.5 text-sm text-foreground focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
                >
                  <option value="">(none)</option>
                  {typeLimits.map((tl) => (
                    <option key={tl.name} value={tl.name}>
                      {tl.name}
                    </option>
                  ))}
                </select>
              )}
            </div>

            {/* Bounds (if type limits exist) */}
            {scheduleTypeLimits && (
              <div>
                <label className="block text-xs font-medium text-muted-foreground">Value Bounds</label>
                <p className="mt-1 text-sm text-foreground">
                  {scheduleTypeLimits.lowerLimit !== undefined
                    ? String(scheduleTypeLimits.lowerLimit)
                    : '-∞'}{' '}
                  to{' '}
                  {scheduleTypeLimits.upperLimit !== undefined
                    ? String(scheduleTypeLimits.upperLimit)
                    : '+∞'}
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Statistics */}
        <div className="border-b bg-card p-4">
          <h3 className="mb-3 text-xs font-semibold uppercase text-muted-foreground">Statistics</h3>

          <div className="grid grid-cols-2 gap-3 text-sm">
            <div>
              <span className="text-muted-foreground">Periods:</span>
              <span className="ml-2 font-medium">{schedule.periods.length}</span>
            </div>
            <div>
              <span className="text-muted-foreground">Category:</span>
              <span className="ml-2 font-medium capitalize">{scheduleInfo?.category || 'other'}</span>
            </div>
            <div className="col-span-2">
              <span className="text-muted-foreground">Referenced by:</span>
              <span className="ml-2 font-medium">{scheduleInfo?.usageCount || 0} object(s)</span>
            </div>
          </div>

          {/* References list */}
          {scheduleInfo && scheduleInfo.referencedBy.length > 0 && (
            <div className="mt-3">
              <p className="text-xs font-medium text-muted-foreground mb-1">Objects using this schedule:</p>
              <div className="max-h-24 overflow-y-auto rounded border border-border bg-muted p-2 text-xs">
                {scheduleInfo.referencedBy.slice(0, 10).map((ref, i) => (
                  <div key={i} className="truncate text-muted-foreground">
                    {ref.objectClass} - {ref.objectName}
                  </div>
                ))}
                {scheduleInfo.referencedBy.length > 10 && (
                  <div className="text-muted-foreground/70 italic">
                    ...and {scheduleInfo.referencedBy.length - 10} more
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Validation */}
        {validation && (validation.errors.length > 0 || validation.warnings.length > 0) && (
          <div className="border-b bg-card p-4">
            <h3 className="mb-3 text-xs font-semibold uppercase text-muted-foreground">Validation</h3>

            <div className="space-y-2">
              {validation.errors.map((error, i) => (
                <ValidationItem
                  key={`error-${i}`}
                  type="error"
                  message={error.message}
                  {...(error.suggestion !== undefined && { suggestion: error.suggestion })}
                />
              ))}
              {validation.warnings.map((warning, i) => (
                <ValidationItem
                  key={`warning-${i}`}
                  type="warning"
                  message={warning.message}
                  {...(warning.suggestion !== undefined && { suggestion: warning.suggestion })}
                />
              ))}
            </div>
          </div>
        )}

        {/* Quick Actions */}
        {!readOnly && (
          <div className="p-4">
            <h3 className="mb-3 text-xs font-semibold uppercase text-muted-foreground">Quick Actions</h3>

            <div className="space-y-2">
              <button
                onClick={onDuplicate}
                className="flex w-full items-center gap-2 rounded-md border border-border px-3 py-2 text-sm text-foreground hover:bg-muted"
              >
                <svg className="h-4 w-4" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M16 1H4c-1.1 0-2 .9-2 2v14h2V3h12V1zm3 4H8c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h11c1.1 0 2-.9 2-2V7c0-1.1-.9-2-2-2zm0 16H8V7h11v14z" />
                </svg>
                Duplicate Schedule
              </button>

              <button
                onClick={onDelete}
                className="flex w-full items-center gap-2 rounded-md border border-destructive/50 px-3 py-2 text-sm text-destructive hover:bg-destructive/10"
              >
                <svg className="h-4 w-4" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z" />
                </svg>
                Delete Schedule
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
